import { useState, useRef, useEffect } from "react";
import { Play, Pause, Volume2, VolumeX } from "lucide-react";

// Custom Audio Player Component
const CustomAudioPlayer = ({ url, transcription, onClick }) => {
    // Keep track of the currently playing audio element across all instances
    // This module-scoped variable ensures only one <audio> plays at a time
    // eslint-disable-next-line no-redeclare
    /* global window */
    if (typeof window !== "undefined" && !window.__sm_currentlyPlayingAudio__) {
        window.__sm_currentlyPlayingAudio__ = null;
    }

    const [isPlaying, setIsPlaying] = useState(false);
    const [duration, setDuration] = useState(0);
    const [currentTime, setCurrentTime] = useState(0);
    const [isMuted, setIsMuted] = useState(false);
    const audioRef = useRef(null);
    const progressBarRef = useRef(null);

    useEffect(() => {
        const audio = audioRef.current;
        if (!audio) return;

        const setAudioData = () => {
            setDuration(audio.duration);
        };

        const setAudioTime = () => {
            setCurrentTime(audio.currentTime);
        };

        const handleEnded = () => {
            setIsPlaying(false);
            setCurrentTime(0);
            if (typeof window !== "undefined" && window.__sm_currentlyPlayingAudio__ === audio) {
                window.__sm_currentlyPlayingAudio__ = null;
            }
        };

        const ensureSinglePlayback = () => {
            if (typeof window === "undefined") return;
            const current = window.__sm_currentlyPlayingAudio__;
            if (current && current !== audio) {
                try {
                    current.pause();
                } catch (_) {
                    // no-op: safe failure
                }
            }
            window.__sm_currentlyPlayingAudio__ = audio;
            setIsPlaying(true);
        };

        // Add event listeners
        audio.addEventListener('loadedmetadata', setAudioData);
        audio.addEventListener('timeupdate', setAudioTime);
        audio.addEventListener('ended', handleEnded);
        audio.addEventListener('play', ensureSinglePlayback);

        return () => {
            audio.removeEventListener('loadedmetadata', setAudioData);
            audio.removeEventListener('timeupdate', setAudioTime);
            audio.removeEventListener('ended', handleEnded);
            audio.removeEventListener('play', ensureSinglePlayback);
            if (typeof window !== "undefined" && window.__sm_currentlyPlayingAudio__ === audio) {
                // Clear the global pointer if this instance is unmounting
                window.__sm_currentlyPlayingAudio__ = null;
            }
        };
    }, []);

    const togglePlay = () => {
        const audio = audioRef.current;
        if (!audio) return;

        if (audio.paused) {
            // Ensure any other audio stops before playing this one
            if (typeof window !== "undefined") {
                const current = window.__sm_currentlyPlayingAudio__;
                if (current && current !== audio) {
                    try {
                        current.pause();
                    } catch (_) {
                        // no-op
                    }
                }
                window.__sm_currentlyPlayingAudio__ = audio;
            }
            const playPromise = audio.play();
            if (playPromise && typeof playPromise.then === "function") {
                playPromise.then(() => setIsPlaying(true)).catch(() => {
                    // Autoplay might be blocked; keep UI consistent
                    setIsPlaying(false);
                });
            } else {
                setIsPlaying(true);
            }
        } else {
            audio.pause();
            setIsPlaying(false);
            if (typeof window !== "undefined" && window.__sm_currentlyPlayingAudio__ === audio) {
                window.__sm_currentlyPlayingAudio__ = null;
            }
        }
    };

    const toggleMute = () => {
        audioRef.current.muted = !isMuted;
        setIsMuted(!isMuted);
    };

    const handleProgressChange = (e) => {
        const time = Number(e.target.value);
        audioRef.current.currentTime = time;
        setCurrentTime(time);
    };

    const formatTime = (time) => {
        if (isNaN(time)) return "0:00";
        const minutes = Math.floor(time / 60);
        const seconds = Math.floor(time % 60);
        return `${minutes}:${seconds.toString().padStart(2, '0')}`;
    };

    return (
        <div className="flex flex-col gap-2 p-3 bg-gray-50 rounded-lg border border-gray-200 w-full max-w-[280px]" onClick={onClick}>
            <audio ref={audioRef} src={url} preload="metadata" />

            <div className="flex flex-row items-end gap-3">
                <button
                    onClick={togglePlay}
                    className="p-2 rounded-full bg-purple-600 text-white hover:bg-purple-700 transition-colors flex-shrink-0"
                >
                    {isPlaying ? <Pause size={16} fill="currentColor" /> : <Play size={16} fill="currentColor" className="" />}
                </button>

                <div className="flex flex-col gap-1 ">
                    <input
                        type="range"
                        min={0}
                        max={duration || 0}
                        value={currentTime}
                        onChange={handleProgressChange}
                        className="w-full h-1 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-purple-600"
                    />
                    <div className="flex justify-between text-[10px] text-gray-500 font-medium">
                        <span>{formatTime(currentTime)}</span>
                        <span>{formatTime(duration)}</span>
                    </div>
                </div>

                <button
                    onClick={toggleMute}
                    className="p-1.5 text-gray-500 hover:bg-gray-200 rounded-full transition-colors"
                >
                    {isMuted ? <VolumeX size={16} /> : <Volume2 size={16} />}
                </button>
            </div>

            {transcription && (
                <div className="mt-1 px-1">
                    <p className="text-[11px] text-gray-600 italic leading-relaxed border-l-2 border-purple-300 pl-2">
                        "{transcription}"
                    </p>
                </div>
            )}
        </div>
    );
};

export default CustomAudioPlayer;